import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Inbox,
  LayoutList,
  Paperclip,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { AttachmentPreview } from "../components/AttachmentPreview";

import { useAuth } from "#/context/AuthProvider";
import { leaveApi } from "../api";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { LeavePagination } from "../components/LeavePagination";
import { LeaveStatusBadge } from "../components/LeaveStatusBadge";
import {
  Btn,
  EmptyState,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "../components/LeaveUI";
import { useAsyncData, useMutation, usePagination } from "../hooks";
import type { CurrentUserResponse } from "../../auth/types";
import type { LeaveApplicationSummary } from "../types";

type Tab = "inbox" | "all";

export default function LeaveApprovalsPage() {
  const { hasPermission, hasRole, currentUser } = useAuth();
  const canManage = hasRole("SUPER_ADMIN") || hasPermission("HR_LEAVE_MANAGE");

  const [tab, setTab] = useState<Tab>("inbox");
  const pagination = usePagination();

  const inbox = useAsyncData(
    () => leaveApi.approvalInbox(pagination.page, pagination.size),
    [tab, pagination.page, pagination.size],
  );

  const allPending = useAsyncData(
    () =>
      canManage
        ? leaveApi.pendingApplications(pagination.page, pagination.size)
        : Promise.resolve(null),
    [tab, pagination.page, pagination.size],
  );

  const activeData = tab === "inbox" ? inbox.data : allPending.data;
  const loading    = tab === "inbox" ? inbox.loading : allPending.loading;
  const error      = tab === "inbox" ? inbox.error : allPending.error;
  const reload     = tab === "inbox" ? inbox.reload : allPending.reload;

  function switchTab(next: Tab) {
    setTab(next);
    pagination.reset();
  }

  return (
    <div className="space-y-6">
      <LeavePageHeader
        breadcrumb="Leave Management"
        title="Approvals"
        actions={
          <div className="flex items-center gap-2">
            <LeaveBackButton to="/hr/leave" label="Dashboard" />
            <Btn
              variant="secondary"
              size="md"
              icon={RefreshCw}
              onClick={reload}
            >
              Refresh
            </Btn>
          </div>
        }
      />

      {/* Tab bar */}
      <div className="flex gap-1 rounded-2xl border bg-white p-1.5 shadow-sm">
        <TabBtn
          active={tab === "inbox"}
          icon={Inbox}
          label="My Inbox"
          count={inbox.data?.totalElements}
          onClick={() => switchTab("inbox")}
        />
        {canManage && (
          <TabBtn
            active={tab === "all"}
            icon={LayoutList}
            label="All Pending"
            count={allPending.data?.totalElements}
            onClick={() => switchTab("all")}
          />
        )}
      </div>

      {loading && <LeaveLoading label="Loading approvals…" />}
      {error   && <LeaveError message={error} onRetry={reload} />}

      {!loading && activeData && (
        <>
          <ApprovalsTable
            items={activeData.content}
            onAction={reload}
            canManage={canManage}
            currentUser={currentUser}
            isInbox={tab === "inbox"}
          />
          <LeavePagination
            page={activeData}
            onPageChange={pagination.setPage}
            onSizeChange={pagination.setSize}
          />
        </>
      )}
    </div>
  );
}

// ─── Attachment cell ──────────────────────────────────────────────────────────

function AttachmentCell({ item }: { item: LeaveApplicationSummary }) {
  // Required but missing
  if (item.requiresAttachment && !item.hasAttachment) {
    return (
      <span
        title="This leave type requires a supporting document, but none was uploaded"
        className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700"
      >
        <AlertTriangle className="h-3 w-3" />
        Missing
      </span>
    );
  }

  // Has a document — show authenticated preview
  if (item.hasAttachment && item.attachmentDownloadUrl) {
    return <AttachmentPreview url={item.attachmentDownloadUrl} compact />;
  }

  // Has attachment but no download URL (older backend)
  if (item.hasAttachment) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
        <Paperclip className="h-3 w-3" />
        Attached
      </span>
    );
  }

  return <span className="text-slate-300">—</span>;
}

// ─── Tab button ───────────────────────────────────────────────────────────────

function TabBtn({
  active,
  icon: Icon,
  label,
  count,
  onClick,
}: {
  active: boolean;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
        active
          ? "bg-emerald-600 text-white shadow-sm"
          : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
      {count !== undefined && count > 0 && (
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-bold ${
            active
              ? "bg-white/20 text-white"
              : "bg-slate-100 text-slate-600"
          }`}
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}

// ─── Table ────────────────────────────────────────────────────────────────────

function ApprovalsTable({
  items,
  onAction,
  canManage,
  currentUser,
  isInbox,
}: {
  items: LeaveApplicationSummary[];
  onAction: () => void;
  canManage: boolean;
  currentUser: CurrentUserResponse | null;
  isInbox: boolean;
}) {
  function fmtDate(v?: string | null) {
    if (!v) return "—";
    try {
      return new Date(`${v}T00:00:00`).toLocaleDateString(undefined, {
        day: "numeric", month: "short", year: "numeric",
      });
    } catch { return v; }
  }

  return (
    <Table>
      <THead>
        <TH>Employee</TH>
        <TH>Leave Type</TH>
        <TH>Dates</TH>
        <TH>Days</TH>
        <TH>Step</TH>
        <TH>Status</TH>
        <TH>Docs</TH>
        <TH right>Actions</TH>
      </THead>
      <TBody>
        {items.length === 0 ? (
          <tr>
            <td colSpan={8}>
              <EmptyState
                icon={Inbox}
                title="No pending approvals"
                description="You're all caught up. New applications will appear here."
              />
            </td>
          </tr>
        ) : (
          items.map((item) => (
            <ApprovalRow
              key={item.id}
              item={item}
              onAction={onAction}
              fmtDate={fmtDate}
              canManage={canManage}
              currentUser={currentUser}
              isInbox={isInbox}
            />
          ))
        )}
      </TBody>
    </Table>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function ApprovalRow({
  item,
  onAction,
  fmtDate,
  canManage,
  currentUser,
  isInbox,
}: {
  item: LeaveApplicationSummary;
  onAction: () => void;
  fmtDate: (v?: string | null) => string;
  canManage: boolean;
  currentUser: CurrentUserResponse | null;
  isInbox: boolean;
}) {
  const [comments, setComments]         = useState("");
  const [showPanel, setShowPanel]       = useState(false);
  const [pendingAction, setPendingAction] = useState<"approve" | "reject" | null>(null);

  const approve = useMutation(() => leaveApi.approve(item.id, { comments }));
  const reject  = useMutation(() => leaveApi.reject(item.id, { comments }));

  const isAssignedApprover =
    item.currentApproverUsername != null
      ? currentUser?.username === item.currentApproverUsername
      : isInbox;
  const canAct = canManage || isAssignedApprover;
  const disabledTitle = canAct
    ? undefined
    : item.currentApproverUsername
      ? `Assigned to ${item.currentApproverUsername}`
      : "Not your approval step";

  async function doAction() {
    if (!pendingAction) return;
    try {
      if (pendingAction === "approve") await approve.mutate(undefined as never);
      else                             await reject.mutate(undefined as never);
      setShowPanel(false);
      setPendingAction(null);
      setComments("");
      onAction();
    } catch { /* error shown inline */ }
  }

  function trigger(action: "approve" | "reject") {
    setPendingAction(action);
    setShowPanel(true);
  }

  const actionError = approve.error || reject.error;
  const isWorking   = approve.loading || reject.loading;

  return (
    <>
      <TR>
        {/* Employee */}
        <TD>
          <div className="font-semibold text-slate-800">{item.employeeName}</div>
          <div className="text-xs text-slate-400">{item.departmentName ?? item.applicationNo}</div>
        </TD>

        {/* Leave type */}
        <TD className="font-medium text-slate-700">{item.leaveTypeName}</TD>

        {/* Dates */}
        <TD className="whitespace-nowrap text-slate-500">
          {fmtDate(item.startDate)} → {fmtDate(item.endDate)}
        </TD>

        {/* Days */}
        <TD>
          <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700">
            {item.requestedDays}d
          </span>
        </TD>

        {/* Step */}
        <TD muted className="text-xs">{item.currentStepName || "—"}</TD>

        {/* Status */}
        <TD><LeaveStatusBadge value={item.status} /></TD>

        {/* Docs */}
        <TD>
          <AttachmentCell item={item} />
        </TD>

        {/* Actions */}
        <TD right>
          <div className="flex items-center justify-end gap-1.5">
            <button
              type="button"
              disabled={!canAct}
              title={disabledTitle}
              onClick={() => canAct && trigger("approve")}
              className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                canAct
                  ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  : "cursor-not-allowed bg-slate-100 text-slate-300"
              }`}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Approve
            </button>
            <button
              type="button"
              disabled={!canAct}
              title={disabledTitle}
              onClick={() => canAct && trigger("reject")}
              className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                canAct
                  ? "bg-red-50 text-red-600 hover:bg-red-100"
                  : "cursor-not-allowed bg-slate-100 text-slate-300"
              }`}
            >
              <XCircle className="h-3.5 w-3.5" />
              Reject
            </button>
            <Link
              to={`/hr/leave/requests/${item.id}`}
              className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              View
            </Link>
          </div>
        </TD>
      </TR>

      {/* Inline confirm panel */}
      {showPanel && (
        <tr className={pendingAction === "approve" ? "bg-emerald-50/60" : "bg-red-50/60"}>
          <td colSpan={8} className="px-5 py-4">
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className="mb-2 text-xs font-semibold text-slate-500">
                  {pendingAction === "approve" ? "✓ Approving" : "✕ Rejecting"}{" "}
                  <span className="text-slate-700">{item.employeeName}</span> — optional comment:
                </p>
                <input
                  autoFocus
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                  placeholder="Add a comment (optional)…"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") doAction();
                    if (e.key === "Escape") setShowPanel(false);
                  }}
                />
                {actionError && (
                  <p className="mt-1 text-xs font-semibold text-red-600">{actionError}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2 pt-6">
                <Btn
                  variant={pendingAction === "approve" ? "primary" : "danger"}
                  size="sm"
                  loading={isWorking}
                  loadingText="Saving…"
                  onClick={doAction}
                >
                  Confirm
                </Btn>
                <Btn
                  variant="secondary"
                  size="sm"
                  onClick={() => { setShowPanel(false); setPendingAction(null); setComments(""); }}
                >
                  Cancel
                </Btn>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
